import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

// Utils
import { useTranslation } from 'react-i18next';

// Components
import InputField from '../../../atoms/InputField/InputField';
import ToggleCheck from '../../../atoms/ToggleCheck/ToggleCheck';
import SelectField from '../../../atoms/SelectField/SelectField';
import ListWithActions from '../../../organisms/ListWithActions/ListWithActions';

function PackageConfiguration({
  formik, isDataLoading, camps, accommodations, extrasWithQuantities, setExtrasWithQuantities, setExtrasTouched,
}) {
  const { t } = useTranslation();

  const getInput = useCallback((name) => (
    <div className="m-b-15">
      <InputField
        id={`package_${name}`}
        name={name}
        value={formik.values[name]}
        label={t(`packages.form.${name}`)}
        formik={{
          handleChange: formik.handleChange,
          handleBlur: formik.handleBlur,
        }}
        error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
        isRequired
        full
      />
    </div>
  ), [formik]);

  const getSelect = useCallback((name, options, isRequired, isMulti) => (
    <SelectField
      id={name}
      name={name}
      options={options}
      label={t(`packages.form.${name}`)}
      error={formik.errors?.[name] && formik.touched?.[name] ? formik.errors[name] : null}
      setFieldTouched={formik.setFieldTouched}
      setFieldValue={formik.setFieldValue}
      value={formik.values[name]}
      isMulti={isMulti}
      disabled={isDataLoading}
      withTags={isMulti}
      isRequired={isRequired}
    />
  ), [formik]);

  return (
    <div>
      <h3 className="fs-20 m-b-25">{t('packages.sub.informations')}</h3>

      <div className="input_max">
        <div className="m-b-30">
          <ToggleCheck
            labels={[t('packages.form.isActive')]}
            name="isActive"
            onValueChange={(checked) => formik.setFieldValue('isActive', checked)}
            isChecked={formik.values.isActive}
          />
        </div>

        {getInput('name')}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('packages.sub.camp')}
        <span className="fs-16 m-l-8">{`(${t('common.requiredLabel').toLowerCase()})`}</span>
      </h3>

      <div className="input_max">
        {getSelect('campId', camps?.map((camp) => ({ label: camp.name, value: camp.id })), true, false)}
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('packages.sub.extras')}
        <span className="fs-16 m-l-8">{`(${t('common.optional').toLowerCase()})`}</span>
      </h3>

      <div>
        <ListWithActions
          items={extrasWithQuantities.map((ex) => ({ ...ex, max: 20 }))}
          setItems={(id, value) => {
            setExtrasWithQuantities(
              (state) => state.map((item) => (item.id === id ? { ...item, quantity: value } : item)),
            );
          }}
          columns={[t('packages.extras.name'), t('packages.extras.quantity')]}
          setFieldTouched={setExtrasTouched}
        />
      </div>

      <hr className="m-t-30 m-b-40" />

      <h3 className="fs-20 m-b-25">
        {t('packages.sub.accommodation')}
        <span className="fs-16 m-l-8">{`(${t('common.optional').toLowerCase()})`}</span>
      </h3>

      <div className="m-b-30">
        <ToggleCheck
          labels={[t('packages.form.isAccommodationMandatory')]}
          name="isAccommodationMandatory"
          onValueChange={(checked) => formik.setFieldValue('isAccommodationMandatory', checked)}
          isChecked={formik.values.isAccommodationMandatory}
        />
      </div>

      <div className="input_max">
        {getSelect('accommodationIds', accommodations?.map((acc) => ({ label: acc.name, value: acc.id })), false, true)}
      </div>
    </div>
  );
}

PackageConfiguration.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.shape({
      name: PropTypes.string,
      isActive: PropTypes.bool,
      isAccommodationMandatory: PropTypes.bool,
    }),
    handleChange: PropTypes.func,
    handleBlur: PropTypes.func,
    setFieldValue: PropTypes.func,
    setFieldTouched: PropTypes.func,
    errors: PropTypes.shape({}),
    touched: PropTypes.shape({}),
  }).isRequired,
  isDataLoading: PropTypes.bool,
  camps: PropTypes.arrayOf(PropTypes.shape({})),
  accommodations: PropTypes.arrayOf(PropTypes.shape({})),
  extrasWithQuantities: PropTypes.arrayOf(PropTypes.shape({})),
  setExtrasWithQuantities: PropTypes.func,
  setExtrasTouched: PropTypes.func,
};

PackageConfiguration.defaultProps = {
  isDataLoading: false,
  camps: [],
  accommodations: [],
  extrasWithQuantities: [],
  setExtrasWithQuantities: () => {},
  setExtrasTouched: () => {},
};

export default PackageConfiguration;
